import { defineStore } from 'pinia'
import {
	commitmentKind,
	commitmentQuotes,
	commitmentStage,
	pastFirstRx,
	type CancelOutcome,
} from '@/cancelFlow/commitment'
import { useSubscriptionStore } from '@/store/subscription'

interface CancelFlowState {
	outcome: CancelOutcome | null
}

/**
 * Commitment cancel flow (DEV-35727): which branch the plan is on, what its screens offer, and how
 * the cancel ended.
 *
 * `kind` / `stage` / `quotes` are views over the subscription store (pure logic in `src/cancelFlow/`):
 * `kind` — monthly commitment or paid in full (`null` → not a commitment, the classic flow).
 * `outcome` is the one piece of state: the backend answer the receipt (`/cancel/done`) renders,
 * snapshotted at the cancel call because the subscription loses part of it afterwards. Persisted to
 * `sessionStorage` like the survey, so a reload on the receipt keeps its copy; cleared at the same
 * `/status` entry points as `survey.startFlow()`. `null` → `/cancel/done` shows the classic receipt,
 * which is also what a non-commitment plan always gets.
 */
export const useCancelFlowStore = defineStore('cancelFlow', {
	state: (): CancelFlowState => ({
		outcome: null,
	}),
	getters: {
		kind() {
			return commitmentKind(useSubscriptionStore().subscription)
		},
		stage() {
			const subs = useSubscriptionStore()
			return commitmentStage({
				kind: commitmentKind(subs.subscription),
				rxUnderReview: subs.isRxUnderReview,
				pastFirstRx: pastFirstRx(subs.subscription),
				remainIterations: subs.subscription?.remain_iterations,
			})
		},
		quotes() {
			return commitmentQuotes(useSubscriptionStore().subscription)
		},
	},
	actions: {
		setOutcome(outcome: CancelOutcome) {
			this.outcome = outcome
		},
		reset() {
			this.outcome = null
		},
	},
	persist: {
		storage: sessionStorage,
		key: 'med-account-cancel-flow',
	},
})
